import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRecentOrders } from '../api/orderService';
import { useAddress } from '../contexts/AddressContext';
import { useAddressAffordance } from '../contexts/AddressAffordanceContext';
import useTranslation from '../hooks/useTranslation';
import { getCurrentServiceUserId } from '../services/OperationTimeService';
import { RecentAddressOrder } from '../types/order';
import {
  getHomeOrderAffordanceName,
  getOrderStatusDisplay,
  getRemainingDeliveryMinutes,
  selectHomeOrderAffordanceOrder,
} from '../utils/orderDisplayUtils';

interface HomeRecentOrderAffordanceProps {
  onVisibilityChange?: (isVisible: boolean) => void;
}

const HomeRecentOrderAffordance: React.FC<HomeRecentOrderAffordanceProps> = ({
  onVisibilityChange,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { address } = useAddress();
  const { showAddressAffordance } = useAddressAffordance();
  const [orders, setOrders] = useState<RecentAddressOrder[]>([]);
  const [now, setNow] = useState(() => new Date());

  const stayId = address?.stay?.stay_id?.trim();
  const serviceUserId = getCurrentServiceUserId()?.trim();

  const loadOrders = useCallback(async () => {
    if (!stayId && !serviceUserId) {
      setOrders([]);
      return;
    }

    try {
      const response = await getRecentOrders({ stayId, serviceUserId });
      setOrders(response.orders);
    } catch (error) {
      console.error('[HomeRecentOrderAffordance] Failed to load recent orders:', error);
      setOrders([]);
    }
  }, [stayId, serviceUserId]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(new Date());
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const selectedOrder = useMemo(
    () => selectHomeOrderAffordanceOrder(orders, now),
    [orders, now],
  );

  const storeName = selectedOrder ? getHomeOrderAffordanceName(selectedOrder) : '';
  const remainingMinutes = selectedOrder?.expectedDeliveryCompletedAt
    ? getRemainingDeliveryMinutes(selectedOrder.expectedDeliveryCompletedAt, now)
    : null;
  const shouldRender = Boolean(
    (stayId || serviceUserId) &&
    !showAddressAffordance &&
    selectedOrder &&
    selectedOrder.expectedDeliveryCompletedAt &&
    storeName &&
    remainingMinutes !== null &&
    remainingMinutes > 0,
  );

  useEffect(() => {
    onVisibilityChange?.(shouldRender);
  }, [onVisibilityChange, shouldRender]);

  if (!shouldRender || !selectedOrder || remainingMinutes === null) {
    return null;
  }

  const status = getOrderStatusDisplay(selectedOrder.status);
  const remainingText = remainingMinutes <= 1
    ? t('orders.affordance.arrivingSoon')
    : t('orders.affordance.remainingMinutesCompact', { minutes: remainingMinutes });

  return (
    <div className="fixed bottom-[76px] left-1/2 z-40 w-full max-w-mobile -translate-x-1/2 px-4">
      <button
        type="button"
        aria-label={t('orders.affordance.viewOrders')}
        onClick={() => navigate('/orders')}
        className="flex w-full items-center gap-2 rounded-2xl border border-red-100 bg-white px-3 py-2.5 text-left shadow-lg transition-colors hover:bg-red-50"
      >
        <span className="shrink-0 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600">
          {t(status.labelKey)}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
          {storeName}
        </span>
        <span className="shrink-0 rounded-full bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-600">
          {remainingText}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 shrink-0 text-gray-400"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
};

export default HomeRecentOrderAffordance;
